import { useState, useEffect } from 'react';

import { convertBase64ToUrl } from '@/utils/file.utils';

/**
 * Hook para gestionar la visualización de PDFs.
 * 
 * Problema: Los navegadores limitan la longitud de las URLs. Pasar un Base64 
 * muy largo directamente al `src` de un `<iframe>` puede hacer que el visor falle.
 * 
 * Solución: Convierte el Base64 en una `Blob URL` (ej. `blob:http...`). 
 * Esto genera una referencia corta y eficiente en memoria que evita bloqueos 
 * al manejar archivos pesados.
 */
export const usePdfViewer = () => {
	const [pdfData, setPdfData] = useState<{ url: string; base64: string } | undefined>();
	const [isLoading, setIsLoading] = useState(false);

	const loadPdf = async (fetcher: () => Promise<string | undefined> | string | undefined) => {
		setIsLoading(true);
		try {
			const base64 = await fetcher();

			if (!base64) {
				setPdfData(undefined);
				return;
			}

			const url = convertBase64ToUrl(base64);

			if (url) {
				setPdfData({ url, base64 });
			}
		} catch (error) {
			console.error('Error en usePdfViewer:', error);
			setPdfData(undefined);
		} finally {
			setIsLoading(false);
		}
	};

	// 2. Efecto de Limpieza: Revoca la URL para liberar memoria RAM
	useEffect(
		() => () => {
			if (pdfData?.url) {
				URL.revokeObjectURL(pdfData.url);
			}
		},
		[pdfData?.url],
	);

	return {
		url: pdfData?.url,
		base64: pdfData?.base64,
		isLoading,
		loadPdf,
		unloadPdf: () => setPdfData(undefined),
	};
};
